/**
 * Product Template Script
 * ------------------------------------------------------------------------------
 * A file that contains scripts highly couple code to the Product template.
 *
 * @namespace product
 */

/* global theme, YT */
import {load, register} from '@shopify/theme-sections';
import {ProductForm , getUrlWithVariant} from '@shopify/theme-product-form';
import {formatMoney} from '@shopify/theme-currency';
import Swiper, { Navigation, Thumbs, Pagination } from 'swiper';
import Drift from 'drift-zoom';

// Style includes
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/free-mode';
import 'swiper/css/a11y';
import 'swiper/css/keyboard';
import 'swiper/css/thumbs';

const selectors = {
    addToCart: '[data-add-to-cart]',
    addToCartText: '[data-add-to-cart-text]',
    comparePrice: '[data-compare-price]',
    comparePriceText: '[data-compare-text]',
    originalSelectorId: '[data-product-select]',
    priceWrapper: '[data-price-wrapper]',
    productImageWrapper: '[data-product-image-wrapper]',
    productFeaturedImage: '[data-product-featured-image]',
    productJson: '[data-product-page]',
    productPrice: '[data-product-price]',
    productThumbnailsSlider: 'carousel--product-thumbnails',
    productFeatureSlider: 'carousel--featured-product',
    productSlides: '.swiper-wrapper .swiper-slide',
    productThumbs: '[data-product-single-thumbnail]',
    productForm: '[data-product-form]',
    radioOptionSelector: '[data-radio-option-selector]',
    singleOptionSelector: '[data-single-option-selector]',
    productMediaWrapper: '[data-product-single-media-wrapper]',
    productMediaTypeVideo: '[data-product-media-type-video]',
    productMediaTypeModel: '[data-product-media-type-model]',
    variantOptionLabels: '.swatch__option label',
    formIncrement: '.form__increment',
    quantity: '#Quantity',
    productSwiperThumbs: null,
    productSwiperFeature: null,
    notifyCartButton: '.button__notify-restock'
};

const cssClasses = {
    activeThumbnail: 'active-thumbnail',
    hide: 'hide',
};

document.addEventListener('DOMContentLoaded', () => {
    /**
     * Product section constructor. Runs on page load as well as Theme Editor
     * `section:load` events.
     * @param {string} container - selector for the section container DOM element
    */
    register('product', {
        async onLoad() {
            const productFormElement = document.querySelector(selectors.productForm);
            const productHandle      = productFormElement.dataset.productHandle;
            const selectedVariantId  = productFormElement.dataset.onloadVariantId;

            // Stop parsing if we don't have the product json script tag when loading
            // section in the Theme Editor
            if (!this.container.querySelector(selectors.productJson)?.innerHTML) {
                return;
            }

            await fetch(`${Shopify.routes.root}products/${productHandle}.js`)
                .then(response => {
                    return response.json();
                })

                .then(productJSON => {
                    this.product = productJSON;
                    this.productForm = new ProductForm(productFormElement, this.product, {
                        onOptionChange: this.onFormOptionChange.bind(this),
                        onQuantityChange: this.onFormQuantityChange.bind(this)
                    });
                });

            this.container.addEventListener('chooseNewSwatch', this.onForcedSwatchChange.bind(this));

            this.currentVariant = this.productForm.product.variants.filter(variant => variant.id == selectedVariantId)[0];
            this.featuredImage = document.querySelector(selectors.productFeaturedImage);
            this.swiperSlides = this.featuredImage.querySelectorAll(selectors.productSlides);

            // this.initProductCarousel();

            const breakpoint = window.matchMedia( '(min-width:1024px)' );

            const checkBreakpoint = function(element) {
                if ( breakpoint.matches === true ) {
                    if ( selectors.productSwiperThumbs !== null ) {
                        selectors.productSwiperThumbs.destroy( true, true );
                    }
                    if ( selectors.productSwiperFeature !== null ) {
                        selectors.productSwiperFeature.destroy( true, true );
                    }

                    element.container.classList.add('product--grid');
                    element.container.classList.add('product-images--show');
                    return;
                } else if ( breakpoint.matches === false ) {
                    element.container.classList.remove('product--grid');
                    element.container.classList.add('product-images--show');
                    return element.initProductCarousel();
                }
            };
            breakpoint.addEventListener('change', () => checkBreakpoint(this));
            checkBreakpoint(this);

            this.initProductVideo();
            this.initVariantTitles();
            this.initVariantHoverWatch();
            this.updatePickupAvailability(this.currentVariant);
            this.initStickyCart();
            this.initProductZoom();
            this.container
                .querySelectorAll('.form__field--radio-width input[data-single-option-selector]')
                .forEach(input => {
                    input.addEventListener('change', this.onForcedSwatchChange.bind(this));
                });
        },

        onFormOptionChange(event) {
            const currentVariant = {variant: event.dataset.variant, target: this.container};
            if (currentVariant.variant) {
                this.currentVariant = currentVariant.variant;
            }

            this.updatePDPInformation(currentVariant);
            this.updateProductPrices(currentVariant);
            this.updateURLwithVariant(currentVariant);

            if (this.featuredImage && this.swiperSlides.length > 1) {
                this.updateImages(currentVariant);
            }
        },

        // toggleWideIndicator(isWide) {
        //     const indicators = this.container.querySelectorAll('.form__option-label--size .wide-indicator');
        //     indicators.forEach(span => {
        //         span.style.display = isWide ? 'inline' : 'none';
        //     });
        //     },

        onForcedSwatchChange() {
            const swatchOptions = [...document.querySelectorAll('.swatch__option')];

            // Find all inputs (radio or select) with data-single-option-selector that are checked/selected
            const selectedOptions = [];

            // Get all selected radio inputs inside swatch__option
            swatchOptions.forEach(optionEl => {
                const radio = optionEl.querySelector('input[type="radio"]:checked');
                if (radio) {
                    // data-index format: "option1", "option2", etc.
                    const optionIndex = parseInt(radio.dataset.index.replace('option', '')) - 1;
                    selectedOptions[optionIndex] = radio.value;
                }
            });

            // Also get selected <select> inputs with data-single-option-selector (if any)
            const selectInputs = [...document.querySelectorAll('select[data-single-option-selector]')];
            selectInputs.forEach(select => {
                const optionIndex = parseInt(select.dataset.index.replace('option', '')) - 1;
                selectedOptions[optionIndex] = select.value;
            });

            // If some options are undefined, fill with empty string (optional)
            for (let i = 0; i < this.productForm.product.options.length; i++) {
                if (typeof selectedOptions[i] === 'undefined') selectedOptions[i] = '';
            }

            // Find variant that matches all selected options exactly
            const matchedVariant = this.productForm.product.variants.find(variant => {
                return variant.options.every((optValue, i) => optValue === selectedOptions[i]);
            });

            if (matchedVariant) {
                this.currentVariant = matchedVariant;
            } else {
                // fallback: select first available variant
                this.currentVariant = this.productForm.product.variants[0];
            }

            const currentVariantEvent = { variant: this.currentVariant, target: this.container };

            this.updatePDPInformation(currentVariantEvent);
            this.updateProductPrices(currentVariantEvent);
            this.updateURLwithVariant(currentVariantEvent);

            if (this.featuredImage && this.swiperSlides.length > 1) {
                this.updateImages(currentVariantEvent);
            }

            const widthInput = document.querySelector('input[data-index="option2"]:checked'); // Adjust if width is not option2
            const isWide = widthInput && widthInput.value.toLowerCase().includes('wide');

            document.querySelectorAll('.wide-indicator').forEach(indicator => {
                indicator.style.display = isWide ? 'inline' : 'none';
            });
        },

        onFormQuantityChange(event) {
            const currentVariant = {variant: event.dataset.variant, target: this.container};
            this.updateProductPrices(currentVariant);
        },

        initVariantTitles() {
            this.changeVariantTitles(this.currentVariant);
        },

        updateURLwithVariant(event) {
            const variant = event.variant;

            if (!variant) return;

            const url = getUrlWithVariant(window.location.href, variant.id);
            window.history.replaceState({ path: url }, '', url);
        },

        updatePDPInformation(event) {
            this.updateMasterSelect(event);
            this.updateAddToCartState(event);
            this.changeVariantTitles(event);
            this.updatePickupAvailability(event?.variant);
        },

        updateImages(evt) {
            const variant = evt.variant;

            if (typeof variant.featured_media != 'undefined') {

                const imageId = this.id + '-' + variant.featured_media.id;

                this.swapImage(imageId);
            }
        },

        updatePickupAvailability(variant) {
            const oldContainer = document.querySelector('[data-store-availability-container]');
            if (!oldContainer) {
                return;
            }
            oldContainer.innerHTML = '';
            fetch(window.Shopify.routes.root + `variants/${variant?.id}/?section_id=pickup-availability`)
                .then(response => response.text())
                .then(text => {
                    const container = document.querySelector('[data-store-availability-container]');
                    const pickupAvailabilityHTML = new DOMParser()
                        .parseFromString(text, 'text/html')
                        .querySelector('.shopify-section');

                    container.appendChild(pickupAvailabilityHTML);
                    const newHeight = container.offsetHeight;
                    const accordionContentContainer = document.querySelector('#accordion--availability--content');
                    if (accordionContentContainer.classList.contains('is-open')) {
                        accordionContentContainer.style.height = `${newHeight}px`;
                    }
                })
                .catch(e => {
                    console.error(e);
                });

        },

        updateMasterSelect(event) {
            const variant = event.variant;

            if (!variant) return;

            this.container.querySelector(selectors.originalSelectorId).value = variant.id;
        },

        updateAddToCartState(event) {
            const variant = event.variant;
            const addToCartButtons = this.container.querySelectorAll(selectors.addToCart);
            const notifyCartButtons = this.container.querySelectorAll(selectors.notifyCartButton);
            const productForm = this.container.querySelector(selectors.productForm);
            const hasBIS = productForm?.dataset.hasBis === 'true';

            if (variant) {
                this.container.querySelector(selectors.priceWrapper).classList.remove(cssClasses.hide);

                // Hide the BIS button only if BIS is enabled
                if (hasBIS) {
                    notifyCartButtons.forEach((button) => {
                        button.classList.add(cssClasses.hide);
                    });
                }
            } else {
                addToCartButtons.forEach((button) => {
                    button.disabled = true;
                    button.querySelector(selectors.addToCartText).innerHTML = theme.strings.unavailable;
                    button.classList.add(cssClasses.hide);
                });
                this.container.querySelector(selectors.priceWrapper).classList.add(cssClasses.hide);

                // Display the BIS button only if BIS is enabled
                if (hasBIS) {
                    notifyCartButtons.forEach((button) => {
                        button.classList.remove(cssClasses.hide);
                        button.classList.remove('button--disabled');
                    });
                }

                return;
            }

            if (variant.available) {
                addToCartButtons.forEach((button) => {
                    button.disabled = false;
                    button.classList.remove(cssClasses.hide);
                    button.querySelector(selectors.addToCartText).innerHTML = theme.strings.addToCart;
                });

                // Hide the BIS button only if BIS is enabled
                if (hasBIS) {
                    notifyCartButtons.forEach((button) => {
                        button.classList.add(cssClasses.hide);
                        button.classList.add('button--disabled');
                    });
                }
            } else {
                addToCartButtons.forEach((button) => {
                    button.classList.add(cssClasses.hide);
                    button.disabled = true;
                    button.querySelector(selectors.addToCartText).innerHTML = theme.strings.soldOut;
                });

                // Display the BIS button only if BIS is enabled
                if (hasBIS) {
                    notifyCartButtons.forEach((button) => {
                        button.classList.remove(cssClasses.hide);
                        button.classList.remove('button--disabled');
                    });
                }
            }
        },

        changeVariantTitles() {
            document.querySelectorAll('.swatch__option input[data-single-option-selector]:checked').forEach((input) => {
                const swatchOptionLabel = input.closest('.swatch__option')?.querySelector('.form__option-label:not(.form__option-label--hidden)');
                const container = input.closest('.form__element');
                const optionId = container?.id;

                if (!swatchOptionLabel || !optionId) return;

                // Now safely find the subtitle
                const titleEl = document.querySelector(`.swatch__title[data-swatch-option="${optionId}"]`);
                const subtitle = titleEl?.querySelector('.swatch__subtitle');

                if (subtitle) {
                    const labelText = swatchOptionLabel.dataset.label ?? swatchOptionLabel.textContent;
                    subtitle.textContent = labelText;
                }

                // Sticky values update (safely)
                const stickyValue = document.querySelector('.sticky__option-value');
                const mobileStickyValue = document.querySelector('.product-sticky--mobile .sticky__option-value');

                if (stickyValue) {
                    stickyValue.textContent = swatchOptionLabel.dataset.label;
                }

                if (mobileStickyValue) {
                    mobileStickyValue.textContent = swatchOptionLabel.dataset.label;
                }
            });

            const skuElement = document.querySelector('[data-product-sku]');
            const variantSelect = document.querySelector('select[data-product-select]');

            if (skuElement && variantSelect) {
                const selectedOption = variantSelect.options[variantSelect.selectedIndex];
                const selectedVariantSku = selectedOption.dataset.sku;

                if (selectedVariantSku && selectedVariantSku !== 'null') {
                    skuElement.textContent = selectedVariantSku;
                } else {
                    skuElement.textContent = ''; // or remove the whole `.product__sku` if needed
                }
            }
        },

        tempChangeVariantTitle(event) {
            const target = event.currentTarget;
            const newLabel = target.dataset.label ?? target.textContent;

            // Find the closest .form__element
            const container = target.closest('.form__element');

            // Get the ID of the form__element (e.g., "option1")
            const optionId = container?.id;
            if (!optionId) return;

            // Now find the corresponding subtitle using that ID
            const titleElement = document.querySelector(`.swatch__title[data-swatch-option="${optionId}"]`);
            const subtitle = titleElement?.querySelector('.swatch__subtitle');

            if (subtitle) {
                subtitle.textContent = newLabel;
            } else {
                console.warn(`No subtitle found for option ID: ${optionId}`);
            }
        },

        initVariantHoverWatch() {
            const changeVariantTitles = this.changeVariantTitles;
            const tempChangeVariantTitle = this.tempChangeVariantTitle;
            document.querySelectorAll('.swatch__option input[data-single-option-selector] ~ label.form__option-label').forEach((swatch__option) => {
                swatch__option.addEventListener('mouseenter', tempChangeVariantTitle);
                swatch__option.addEventListener('mouseleave', changeVariantTitles);
            });
        },

        updateProductPrices(evt) {
            const variant = evt.variant;
            if (!variant) {
                return false;
            }
            const comparePrice = this.container.querySelector(selectors.comparePrice);
            const comparePriceText = this.container.querySelector(selectors.comparePriceText);
            const quantity = parseInt(this.container.querySelector(selectors.quantity).value);

            this.container.querySelector(selectors.productPrice).innerHTML = formatMoney(variant.price, theme.settingCurrencyFormat);

            const priceWithQuantity = variant.price * quantity;
            document.querySelector('#addToCartPricing').innerHTML = formatMoney(priceWithQuantity, theme.moneyWithoutCurrency);

            if (variant.compare_at_price > variant.price) {
                comparePrice.innerHTML = formatMoney(variant.compare_at_price, theme.settingCurrencyFormat);
                comparePrice.classList.remove(cssClasses.hide);
                comparePriceText.classList.remove(cssClasses.hide);
                this.container.querySelector(selectors.productPrice).classList.add('price__original--sale');
            } else if(comparePrice) {
                comparePrice.innerHTML = '';
                comparePrice.classList.add(cssClasses.hide);
                comparePriceText.classList.add(cssClasses.hide);
                this.container.querySelector(selectors.productPrice).classList.remove('price__original--sale');
            }
        },

        disableVariants(target, valuesToEnable) {
            // Loop through all the Option Selectors and check which one is a swatch selector
            // The target is usually the main product container
            const optionSelector1 = target.querySelectorAll('[data-index="option1"]');
            const optionSelector2 = target.querySelectorAll('[data-index="option2"]');
            const optionSelector3 = target.querySelectorAll('[data-index="option3"]');
            let is_swatch_selector1 = false;
            let is_swatch_selector2 = false;
            let is_swatch_selector3 = false;

            if (optionSelector1.length > 0 && optionSelector1[0].getAttribute('type') == 'radio') {
                is_swatch_selector1 = true;
            }

            if (optionSelector2.length && optionSelector2[0].getAttribute('type') == 'radio') {
                is_swatch_selector2 = true;
            }

            if (optionSelector3.length && optionSelector3[0].getAttribute('type') == 'radio') {
                is_swatch_selector3 = true;
            }

            const option1 = target.querySelectorAll('input[data-index="option1"]');
            const option2 = target.querySelectorAll('input[data-index="option2"]');
            const option3 = target.querySelectorAll('input[data-index="option3"]');

            // If there are 3 option selectors
            if (optionSelector3.length > 0) {

                // If the third option selector is a swatch selector, the available/unavailable state only shows up on this selector based on selected options from Selector 1 and 2.
                if (is_swatch_selector3 == true) {

                    option1.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                    });

                    option2.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                    });

                    option3.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                        if (!valuesToEnable.option3.includes(input.value)) {
                            input.nextElementSibling.classList.add('swatch--disabled');
                        }
                    });

                // If the second option selector is a swatch selector, the available/unavailable state only shows up on this selector based on selected options from Selector 1 and 3.
                } else if (is_swatch_selector2 == true) {

                    option1.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                    });

                    option2.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                        if (!valuesToEnable.option2.includes(input.value)) {
                            input.nextElementSibling.classList.add('swatch--disabled');
                        }
                    });

                // If the first option selector is a swatch selector, the available/unavailable state only shows up on this selector based on selected options from Selector 2 and 3.
                } else if (is_swatch_selector1 == true) {

                    option1.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                        if (!valuesToEnable.option1.includes(input.value)) {
                            input.nextElementSibling.classList.add('swatch--disabled');
                        }
                    });
                }

            // If there are 2 option selectors
            } else if (optionSelector2.length > 0) {

                // If the second option selector is a swatch selector, the available/unavailable state only shows up on this selector based on selected options from Selector 1.
                if (is_swatch_selector2 == true) {

                    option1.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                    });

                    option2.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                        if (!valuesToEnable.option2.includes(input.value)) {
                            input.nextElementSibling.classList.add('swatch--disabled');
                        }
                    });

                // If the first option selector is a swatch selector, the available/unavailable state only shows up on this selector based on selected options from Selector 2.
                } else if (is_swatch_selector1 == true) {

                    option1.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                        if (!valuesToEnable.option1.includes(input.value)) {
                            input.nextElementSibling.classList.add('swatch--disabled');
                        }
                    });
                }
            } else {
                if (is_swatch_selector1 == true) {
                    option1.forEach((option) => {
                        const input = option;

                        const labels = input.parentNode.querySelectorAll('label');

                        labels.forEach(label => {
                            label.classList.remove('swatch--disabled');
                        });
                        if (!valuesToEnable.option1.includes(input.value)) {
                            labels.forEach(label => {
                                label.classList.add('swatch--disabled');
                            });
                        }
                    });
                }
            }
        },

        initStickyCart() {
            const options = {
                rootMargin: '100px 0px 0px 0px',
                threshold: [0,0.01,1]
            };
            const target = document.querySelector('#productStickyContainer');
            const observer = new IntersectionObserver(this.showHideStickyCart, options);

            observer.observe(target);
        },

        showHideStickyCart(entries) {
            let entry = entries[0];
            if (entry.intersectionRatio >= 1) {
                entry.target.classList.remove('product__sticky--show');
                entry.target.classList.add('product__sticky--hide');
            } else if (entry.intersectionRatio <= 0 && entry.boundingClientRect.y <= 0) {
                entry.target.classList.add('product__sticky--show');
                entry.target.classList.remove('product__sticky--hide');
            } else {
                entry.target.classList.add('product__sticky--hide');
                entry.target.classList.remove('product__sticky--show');
            }
        },

        initProductVideo: function() {
            const sectionId = this.id;
            const videoContainers = document.querySelectorAll(selectors.productMediaTypeVideo, this.container);

            videoContainers.forEach((videoContainer) => {
                ProductVideo.init(videoContainer, sectionId);
            });
        },

        initProductZoom: function() {
            if (matchMedia('(pointer:fine)').matches && window.innerWidth> 1023) {
                const driftImgs = document.querySelectorAll('.js--image-zoom');

                driftImgs.forEach((image) => {
                    let container = image.closest('.swiper-slide');

                    // Documentation link for reference: https://github.com/imgix/drift#basic-usage
                    new Drift(image, {
                        containInline: true,
                        inlineContainer: container,
                        inlineOffsetX: 0,
                        inlineOffsetY: 0,
                        inlinePane: true,
                        handleTouch: false,
                        injectBaseStyles: true,
                        hoverDelay: 200,
                    });
                });
            }
        },

        initProductCarousel() {
            // -------------------------------------
            // Carousel Products
            // -------------------------------------

            const productFeatureSwiper = new Swiper('.'+selectors.productFeatureSlider+'', {
                // Optional parameters
                modules: [Navigation, Thumbs, Pagination],
                spaceBetween: 0,

                // // Navigation arrows
                navigation: {
                    nextEl: '.swiper-arrow__container--next',
                    prevEl: '.swiper-arrow__container--prev',
                },
                pagination: {
                    el: '.swiper-pagination',
                    type: 'fraction',
                },
                on: {
                    init: function() {
                        toggleInnerFocus(this);
                    }
                }
            });

            // The "on" event is proper to the Swiper JS library.
            productFeatureSwiper.on('slideChange ', function () {
                // If current slide has video, trigger "mediaVisible" event to play the video. Then if the previous slide has video, trigger the "mediaHidden" event to pause it.
                const previousSlideIndex = productFeatureSwiper.previousIndex,
                    currentSlideIndex = productFeatureSwiper.activeIndex;

                // If current slide has video, trigger "mediaVisible" event to play the video. Then if the previous slide has video, trigger the "mediaHidden" event to pause it.
                const currentMedia = document.querySelector('.product__feature-image .swiper-slide[data-index="'+currentSlideIndex+'"] .product-single__media-wrapper'),
                    previousMedia = document.querySelector('.product__feature-image .swiper-slide[data-index="'+previousSlideIndex+'"] .product-single__media-wrapper');

                previousMedia.dispatchEvent(new Event('mediaHidden'));
                currentMedia.dispatchEvent(new Event('mediaVisible'));
                toggleInnerFocus(this);
            });

            selectors.productSwiperFeature = productFeatureSwiper;
        },

        slideToSlide(slider, slideIndex, transition) {
            if (transition) {
                slider.slideTo(slideIndex);
            } else {
                slider.slideTo(slideIndex, 0);
            }
        },

        // triggering change image when using with Slider
        swapImage(imageId) {
            let newImageId = imageId;

            // If "imageId" is not defined in the function parameter, find it by the current product image
            if (typeof newImageId === 'undefined') {
                newImageId = document.querySelector(
                    `${selectors.productImageWrapper}:not('.${cssClasses.hide}')`
                ).dataset.imageId;
            }

            const thumbnail = document.querySelector(
                `${selectors.productThumbs}[data-thumbnail-id='${newImageId}']`
            );
            const slideIndex = parseInt(thumbnail.parentElement.getAttribute('data-index'));

            // Swipe JS
            this.slideToSlide(selectors.productSwiperThumbs, slideIndex, false);
            this.slideToSlide(selectors.productSwiperFeature, slideIndex, false);
        },

        /**
         * Event callback for Theme Editor `section:unload` event
         */
        onUnload() {
            this.productForm.destroy();
        },

    });



    /**
     * Load Video content on Product Swiper
     */
    const ProductVideo = (() => {
        const videos = {};

        const hosts = {
            html5: 'html5',
            youtube: 'youtube'
        };

        const attributes = {
            enableVideoLooping: 'enable-video-looping',
            videoId: 'video-id'
        };

        // Initialize Video Container
        const init = (videoContainer, sectionId) => {
            if (!videoContainer) {
                return;
            }

            const videoElement = videoContainer.querySelectorAll('iframe, video')[0];
            const mediaId = videoContainer.dataset.mediaId;

            if (!videoElement) {
                return;
            }

            videos[mediaId] = {
                mediaId: mediaId,
                sectionId: sectionId,
                host: hostFromVideoElement(videoElement),
                container: videoContainer,
                element: videoElement,
            };

            const video = videos[mediaId];
            switch (video.host) {
                case hosts.html5:
                    setupPlyrVideos();
                    break;
                case hosts.youtube:
                    setupYouTubeVideos();
                    break;
            }
        };

        // Set Up HTML 5 Players
        const setupPlyrVideos = () => {
            loadVideos(hosts.html5);
        };

        // Set Youtube embedded Videos
        const setupYouTubeVideos = () => {
            window.YT.ready(function() {
                loadVideos(hosts.youtube);
            });
        };

        // Create Video Player
        const createPlayer = (video) => {
            if (video?.player) {
                return;
            }

            const productMediaWrapper = video.container.closest(
                selectors.productMediaWrapper
            );
            const enableLooping = productMediaWrapper.dataset[attributes.enableVideoLooping];

            switch (video.host) {
                case hosts.html5:
                    video.player = new Shopify.Plyr(video.element, {
                        loop: { active: enableLooping }
                    });
                    toggleInnerFocus(selectors.productSwiperFeature);
                    break;
                case hosts.youtube: {
                    const videoId = productMediaWrapper.dataset[attributes.videoId];
                    video.player = new YT.Player(video.element, {
                        videoId: videoId,
                        events: {
                            onStateChange: (event) => {
                                if (event.data === 0 && enableLooping) event.target.seekTo(0);
                            }
                        }
                    });
                    break;
                }
            }

            productMediaWrapper.addEventListener('mediaHidden', () => {
                if (!video.player) return;

                if (video.host === hosts.html5) {
                    video.player.pause();
                }

                if (video.host === hosts.youtube && video.player.pauseVideo) {
                    video.player.pauseVideo();
                }
            });

            productMediaWrapper.addEventListener('mediaVisible', () => {
                if (!video.player) return;

                if (video.host === hosts.html5) {
                    video.player.play();
                }

                if (video.host === hosts.youtube && video.player.playVideo) {
                    video.player.playVideo();
                }
            });
        };

        // Get host source from Video
        const hostFromVideoElement = (video) => {
            if (video.tagName === 'VIDEO') {
                return hosts.html5;
            }

            if (video.tagName === 'IFRAME') {
                if (
                    /^(https?:\/\/)?(www\.)?(youtube\.com|youtube-nocookie\.com|youtu\.?be)\/.+$/.test(
                        video.src
                    )
                ) {
                    return hosts.youtube;
                }
            }
            return null;
        };

        // Load Video Players
        const loadVideos = (host) => {
            for (const videoId in videos) {
                const video = Object.prototype.hasOwnProperty.call(videos, videoId) ? videos[videoId] : null;

                if (video?.host === host) createPlayer(video);
            }
        };

        // Remove Video Players
        const removeSectionVideos = (sectionId) => {
            for (const videoId in videos) {
                const video = Object.prototype.hasOwnProperty.call(videos, videoId) ? videos[videoId] : null;

                if (video?.sectionId === sectionId) {
                    if (video.player) video.player.destroy();
                    delete videos[videoId];
                }
            }
        };

        return {
            init: init,
            hosts: hosts,
            loadVideos: loadVideos,
            removeSectionVideos: removeSectionVideos
        };
    })();

    load('product');
});

/**
 * Slider Accessibility Helper
 */
const toggleInnerFocus = (slider) => {
    const focusableElements = ['iframe', 'input', 'select', 'option', 'textarea', 'button', 'video', 'label', 'a', '.plyr', 'model-viewer'];
    focusableElements.forEach((fosusableElement) => {
        slider?.slides.forEach((slide, index) => {
            if (index === slider.activeIndex) {
                slide.querySelectorAll(fosusableElement).forEach((elem) => {
                    elem?.removeAttribute('tabindex');
                });
            } else {
                slide.querySelectorAll(fosusableElement).forEach((elem) => {
                    if (elem === '.plyr') {
                        elem?.removeAttribute('tabindex');
                    }  else {
                        elem?.setAttribute('tabindex', '-1');
                    }
                });
            }
        });
    });
};
